import { memo, type ReactNode, useMemo } from "react";

import { type TechnicalDetailsDataEntry } from "../ProductDetails/dto/PdpPageProductDataDto";
import { type PdpPageProductData } from "../ProductDetails/models/PdpPageProductData";
import { useIsMinDesktopSizeL, useIsMinTabletSize } from "../common/hooks/useMediaQuery";
import { useUnitMode } from "../feature/UnitMode/useUnitMode";
import { filterTechnicalDetails } from "../feature/UnitMode/unitLists";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { type ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/utils/reducers";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { renderTechValue } from "~/utils/renderTechValue";

const LABEL_COL_WIDTH = 224;

interface ProductInformationTableProps {
  products: ProductDescriptor<PdpPageProductData>[];
  placeholders: number[];
  colSize: number;
}

const getKey = (data: TechnicalDetailsDataEntry) => data.code?.substring(data.code?.lastIndexOf(".") ?? 0) ?? "";

const ProductInformationTable = memo(({ products, placeholders, colSize }: ProductInformationTableProps) => {
  const isTablet = useIsMinTabletSize();
  const isDesktopL = useIsMinDesktopSizeL();
  const { t } = useTranslation();
  const unitType = useUnitMode();

  const getDescription = (data: TechnicalDetailsDataEntry) =>
    data.needsLocalisation ? t(data.description) : data.description;

  const rowDefinitions = useMemo(() => {
    const rowDefinitions: { uid: string; name: ReactNode }[] = [];
    const usedCodes: { [key: string]: boolean } = {};
    products.forEach((descriptor) => {
      const filteredTechDetails = descriptor?.base?.technicalDetails.map((item) =>
        filterTechnicalDetails(item, unitType),
      );
      filteredTechDetails.forEach((techDetail) => {
        techDetail.entries.forEach((row) => {
          const key = getKey(row);
          if (!usedCodes[key]) {
            const description = getDescription(row);
            const showDash = !!((row.abbreviation || row.abbreviationAddition) && description);
            rowDefinitions.push({
              uid: key,
              name: (
                <span>
                  {row.abbreviation}
                  {row.abbreviationAddition && <sub>{row.abbreviationAddition}</sub>}
                  {showDash && " - "}
                  {description}
                </span>
              ),
            });
            usedCodes[key] = true;
          }
        });
      });
    });
    return rowDefinitions;
  }, [products, unitType]);

  const fakeData = useMemo(() => {
    let fakeData = new Array(rowDefinitions.length).fill(0);
    fakeData = fakeData.map((_, index) => ({ index }));
    return fakeData;
  }, [rowDefinitions]);

  const columnConfig: ColumnDef<{ index: number }>[] = useMemo(() => {
    const columnConfig: ColumnDef<{ index: number }>[] = [];

    const labelCol: ColumnDef<{ index: number }> = {
      id: "label",
      header: t("productDetailsPage.tabs.information") as string,
      size: LABEL_COL_WIDTH,
      cell: (cell) => {
        const rowIndex = cell.row.original.index;
        return rowDefinitions[rowIndex]?.name;
      },
    };

    const makeProductCol = (index: number) => {
      const productCol: ColumnDef<{ index: number }> = {
        id: `product-${index}`,
        header: () =>
          !isTablet && index === 0 ? (
            <div style={{ marginLeft: "10px" }}>{t("productDetailsPage.tabs.information") as string}</div>
          ) : (
            ""
          ),
        size: colSize,
        cell: (cell) => {
          const rowIndex = cell.row.original.index;
          const product = products[index];
          const rowDef = rowDefinitions[rowIndex];
          const data = fetchDataForKey(product?.base, rowDef?.uid);

          if (!data || !data.values.length) {
            return "";
          }

          const description = getDescription(data);

          return (
            <>
              {data.values.map((value) => (
                <div className={data.isConverted ? "f-info-color" : undefined}>
                  {!isTablet && <div>{[data.abbreviation, description].filter(isNonNull).join(" - ")}</div>}
                  {renderTechValue(value, data.unitSymbol === "in", data.isConverted)} <span>{data.unitSymbol}</span>
                  <sup>{data.valueFootnotes.length > 1 ? data.valueFootnotes.join(", ") : data.valueFootnotes}</sup>
                </div>
              ))}
            </>
          );
        },
      };
      return productCol;
    };

    if (isTablet) {
      columnConfig.push(labelCol);
    }
    products.forEach((product, index) => {
      columnConfig.push(makeProductCol(index));
    });
    placeholders.forEach((product) => {
      columnConfig.push(makeProductCol(product));
    });

    if (isDesktopL) {
      const len = columnConfig.length;
      columnConfig[len - 1].size = undefined;
    }

    return columnConfig;
  }, [products, isDesktopL]);

  return (
    <TanStackTable
      data={fakeData}
      columns={columnConfig}
      variant={"productComparison"}
      state={{ columnPinning: { left: ["label"] } }}
    />
  );
});

const fetchDataForKey = (product: PdpPageProductData, key: string) => {
  return product?.technicalDetails.flatMap((table) => table.entries).find((row) => getKey(row) === key);
};

export default ProductInformationTable;
